import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useGetOAuthUrl } from '@tinct/core-api';
import { CrmProviderType } from '@tinct/core-api-models';
import { useCurrentWorkspaceUrlPrefix } from '@tinct/core-workspace';

type UseOAuthPopupParams = {
  organizationId: string | undefined;
  provider: CrmProviderType;
};

export const useOAuthPopup = ({ organizationId, provider }: UseOAuthPopupParams) => {
  const queryClient = useQueryClient();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const { mutate: getOAuthUrl, isPending } = useGetOAuthUrl();

  const handleOAuthResult = useCallback(
    (data: { type: string; success: boolean; error?: string | null }) => {
      if (data?.type !== 'oauth-callback') return;
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
      } else if (data.error) {
        toast.error(data.error);
      }
    },
    [queryClient, organizationId]
  );

  // Primary: BroadcastChannel (works even when window.opener is null)
  useEffect(() => {
    const channel = new BroadcastChannel('oauth-callback');
    channel.onmessage = (event) => handleOAuthResult(event.data);
    return () => channel.close();
  }, [handleOAuthResult]);

  // Fallback: window.postMessage (for older browsers). Any window holding a reference to this one
  // can post to it, so only the callback page of our own origin is listened to.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      handleOAuthResult(event.data);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [handleOAuthResult]);

  const openOAuthPopup = useCallback(() => {
    if (!organizationId) return;

    const targetUrl = `${document.location.origin}${workspaceUrl}/settings/integrations/oauth/callback`;

    getOAuthUrl(
      {
        organizationId,
        provider,
        targetUrl,
      },
      {
        onSuccess: (data) => {
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          window.open(
            data.authorizationUrl,
            `${provider.toLowerCase()}-oauth`,
            `width=${width},height=${height},left=${left},top=${top},popup=yes`
          );
        },
      }
    );
  }, [organizationId, provider, workspaceUrl, getOAuthUrl]);

  return {
    openOAuthPopup,
    isPending,
  };
};
